/**
 * API client de Contratos (Bloco F.8).
 *
 * Rotas autenticadas:
 *   GET    /api/contratos?orcamento={id}&situacao=...
 *   GET    /api/contratos/{id}
 *   POST   /api/contratos                          body: {orcamento, template?, dias_validade?}
 *   POST   /api/contratos/{id}/revogar             (forca EXPIRADO)
 *   POST   /api/orcamentos/{id}/gerar-contrato     body: {template_id?, dias_validade?}
 *
 * Rotas publicas (sem auth — chamada direta via fetch, NAO usa `request`):
 *   GET    /publico/contrato/{token}
 *   POST   /publico/contrato/{token}/assinar       body: {nome, email, documento?, aceite_termos}
 *   POST   /publico/contrato/{token}/recusar       body: {motivo}
 */

import { ACCESS_API_URL as API_BASE } from "../config/api";

const PREFIXO = "/api/contratos";

// ---------- Autenticado ----------

export function listarContratos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.orcamento) usp.set("orcamento", params.orcamento);
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO}${qs ? `?${qs}` : ""}`);
}

export function obterContrato(request, id) {
  return request(`${PREFIXO}/${id}`);
}

export function gerarContratoDeOrcamento(request, orcamentoId, { templateId, diasValidade } = {}) {
  const body = {};
  if (templateId) body.template_id = templateId;
  if (diasValidade) body.dias_validade = diasValidade;
  return request(`/api/orcamentos/${orcamentoId}/gerar-contrato`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


/**
 * Security Sprint 0 / C2 (audit 2026-05-21): obtem o link publico de
 * forma explicita e audita evento `contrato_link_copiado`. Substitui a
 * pratica antiga de ler `c.token` direto da listagem (que vazava tokens
 * a qualquer usuario com `contrato:view`).
 *
 * Retorna `{token, url, expira_em}`. URL ja vem pronta do backend.
 *
 * Requer permissao `contrato:gerar` (mesma de gerar contrato).
 */
export function copiarLinkPublicoContrato(request, contratoId) {
  return request(`${PREFIXO}/${contratoId}/copiar-link-publico`, {
    method: "POST",
  });
}

// ---------- Publico (sem auth) ----------

async function _publicFetch(path, init = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  let body = null;
  try { body = await res.json(); } catch (_) { /* sem body */ }
  if (!res.ok) {
    const erro = new Error(body?.detalhe || `HTTP ${res.status}`);
    erro.status = res.status;
    erro.body = body;
    throw erro;
  }
  return body;
}

export function visualizarContratoPublico(token) {
  return _publicFetch(`/publico/contrato/${token}`);
}

export function assinarContratoPublico(token, { nome, email, documento, aceite_termos }) {
  return _publicFetch(`/publico/contrato/${token}/assinar`, {
    method: "POST",
    body: JSON.stringify({
      nome,
      email,
      documento: documento || "",
      aceite_termos: Boolean(aceite_termos),
    }),
  });
}

export function recusarContratoPublico(token, motivo) {
  return _publicFetch(`/publico/contrato/${token}/recusar`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

/**
 * @deprecated Security Sprint 0 / C2 (audit 2026-05-21): tokens de contrato
 * NAO sao mais expostos em serializers internos. Use
 * `copiarLinkPublicoContrato(request, contratoId)` que retorna o link via
 * endpoint dedicado auditado. Esta funcao continua exportada apenas para
 * casos de teste isolado onde o token e' conhecido (gerar contrato +
 * receber token diretamente do POST).
 */
export function urlPublicaDoContrato(token) {
  if (typeof window === "undefined") return `/p/contrato/${token}`;
  return `${window.location.origin}/p/contrato/${token}`;
}
